import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdDeleteOutline, MdEditNote } from "react-icons/md";
import moment from "moment";
import styles from "./Dashboard.module.scss";
import useCloudinary from "../../utils/useCloudinary";
import { getPosts, createPost, updatePost, deletePost } from "../../utils/network/Crud";

const emptyForm = { title: "", content: "", image: "", pinned: false };

const ManagePosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { uploadImage, uploading } = useCloudinary();

  const loadPosts = async () => {
    setLoading(true);
    const result = await getPosts();
    setPosts(result?.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditingId(post.id);
    setForm({
      title: post.title || "",
      content: post.content || "",
      image: post.image || "",
      pinned: !!post.pinned,
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      let imageUrl = form.image;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      const payload = { ...form, image: imageUrl || "" };
      if (editingId) {
        await updatePost(editingId, payload);
        toast.success("Announcement updated");
      } else {
        await createPost(payload);
        toast.success("Announcement published");
      }
      setShowModal(false);
      loadPosts();
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      await deletePost(deleteId);
      toast.success("Announcement removed");
      setDeleteId(null);
      loadPosts();
    } catch (error) {
      toast.error("Failed to remove announcement");
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Announcements</h2>
          <p>Post news &amp; updates for the community to see on the Home page.</p>
        </div>
        <button className={styles.primaryBtn} onClick={openAdd}>
          <MdAdd /> New Announcement
        </button>
      </div>

      <div className={styles.panel}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Posted</th>
                <th>Pinned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Loading…</td>
                </tr>
              ) : posts.length === 0 ? (
                <tr>
                  <td colSpan={5}>No announcements yet.</td>
                </tr>
              ) : (
                posts.map((post) => {
                  const dateVal = post.createdAt?.toDate
                    ? post.createdAt.toDate()
                    : post.createdAt;
                  return (
                    <tr key={post.id}>
                      <td>
                        {post.image ? (
                          <img src={post.image} alt={post.title} className={styles.thumb} />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{post.title}</td>
                      <td>{dateVal ? moment(dateVal).format("DD MMM YYYY") : "—"}</td>
                      <td>
                        {post.pinned && <span className={styles.badgeGold}>Pinned</span>}
                      </td>
                      <td>
                        <button className={styles.iconBtn} onClick={() => openEdit(post)}>
                          <MdEditNote />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                          onClick={() => setDeleteId(post.id)}
                        >
                          <MdDeleteOutline />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Announcement" : "New Announcement"}</h3>
            <form className={styles.formGrid} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Content</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Image (optional)</label>
                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                {form.image && !imageFile && (
                  <img src={form.image} alt="current" className={styles.thumb} style={{ marginTop: 6 }} />
                )}
              </div>
              <label className={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={form.pinned}
                  onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                />
                Pin to top
              </label>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={uploading}>
                  {uploading ? "Saving…" : editingId ? "Save Changes" : "Publish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className={styles.modalOverlay} onClick={() => setDeleteId(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>Remove Announcement?</h3>
            <p style={{ color: "#b6acc9", fontSize: 13 }}>This action cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.secondaryBtn} onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button className={`${styles.primaryBtn} ${styles.dangerBtn}`} onClick={handleDelete}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePosts;
